import React, { useState, useMemo } from 'react';
import { Checkbox } from '../../../components/ui/Checkbox';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ServiceSelectionPanel = ({
  services,
  selectedServices,
  onServiceToggle,
  currencyCode = 'EUR',
  disabled = false,
  title = 'Services disponibles',
  className = '',
  compact = false,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = useMemo(() => {
    const cats = new Set(services?.map(s => s?.category));
    return ['all', ...Array.from(cats)];
  }, [services]);

  const filteredServices = useMemo(() => {
    return services?.filter(service => {
      const matchesSearch = service?.serviceName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                           service?.serviceCode?.toLowerCase()?.includes(searchQuery?.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || service?.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [services, searchQuery, selectedCategory]);

  const formatAmount = (value) => {
    try {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: currencyCode || 'EUR',
        minimumFractionDigits: 2,
      }).format(value || 0);
    } catch {
      return `${value ?? 0} ${currencyCode || ''}`.trim();
    }
  };

  return (
    <div
      className={`bg-card rounded-lg border border-border shadow-card ${
        compact ? 'p-3 sm:p-4' : 'p-6'
      } ${className}`}
    >
      <div className={`flex items-center gap-2 ${compact ? 'mb-3' : 'mb-6'}`}>
        <div className={`bg-primary/10 rounded-lg ${compact ? 'p-1.5' : 'p-2'}`}>
          <Icon name="Briefcase" size={compact ? 18 : 24} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className={`font-semibold text-foreground ${compact ? 'text-base' : 'text-xl font-bold'}`}>
            {title}
          </h2>
          <p className="text-xs text-muted-foreground">
            {selectedServices?.length} sélectionné(s)
          </p>
        </div>
      </div>

      <div className={`space-y-3 ${compact ? 'mb-3' : 'mb-6'}`}>
        <Input
          placeholder="Rechercher un service..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e?.target?.value)}
          leftIcon="Search"
          disabled={disabled}
        />

        <div className="flex flex-wrap gap-1.5">
          {categories?.map(cat => (
            <button
              key={cat}
              type="button"
              onClick={() => setSelectedCategory(cat)}
              className={`rounded-full font-medium transition-colors ${
                compact ? 'px-2.5 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
              } ${
                selectedCategory === cat
                  ? 'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              {cat === 'all' ? 'Tous' : cat}
            </button>
          ))}
        </div>
      </div>

      <div className={`overflow-y-auto ${compact ? 'space-y-1.5 max-h-[420px]' : 'space-y-2 max-h-[600px]'}`}>
        {filteredServices?.length > 0 ? (
          filteredServices?.map(service => {
            const isSelected = selectedServices?.includes(service?.id);
            return (
              <div
                key={service?.id}
                onClick={() => !disabled && onServiceToggle(service?.id)}
                className={`rounded-md border transition-all ${
                  compact ? 'p-2' : 'p-4 rounded-lg border-2'
                } ${
                  disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
                } ${
                  isSelected
                    ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 bg-card'
                }`}
              >
                <div className={`flex gap-2 ${compact ? 'items-center' : 'items-start gap-3'}`}>
                  <Checkbox
                    checked={isSelected}
                    onChange={() => !disabled && onServiceToggle(service?.id)}
                    disabled={disabled}
                    className={compact ? '' : 'mt-1'}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className={`text-foreground truncate ${compact ? 'text-sm font-medium' : 'font-semibold'}`}>
                        {service?.serviceName}
                      </h3>
                      <span className={`shrink-0 text-primary ${compact ? 'text-xs font-semibold' : 'text-sm font-bold'}`}>
                        {formatAmount(service?.unitPrice)}
                      </span>
                    </div>
                    {compact ? (
                      <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                        <span className="truncate">{service?.serviceCode}</span>
                        {service?.category && (
                          <>
                            <span aria-hidden>·</span>
                            <span className="shrink-0">{service.category}</span>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-muted-foreground mb-2">
                          {service?.serviceCode}
                        </p>
                        {service?.description && (
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {service?.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2">
                          <span className="px-2 py-0.5 rounded text-xs bg-muted text-foreground">
                            {service?.category}
                          </span>
                          {service?.isActive && (
                            <span className="flex items-center gap-1 text-xs text-success">
                              <Icon name="Check" size={12} />
                              Actif
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className={`text-center ${compact ? 'py-6' : 'py-12'}`}>
            <Icon name="Search" size={compact ? 32 : 48} className="text-muted-foreground mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Aucun service trouvé</p>
          </div>
        )}
      </div>

      {!compact && filteredServices?.length > 0 && (
        <div className="mt-6 pt-4 border-t border-border">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Total services:</span>
            <span className="font-semibold text-foreground">{filteredServices?.length}</span>
          </div>
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="text-muted-foreground">Sélectionnés:</span>
            <span className="font-semibold text-primary">{selectedServices?.length}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceSelectionPanel;
